import type { MeditationOpening } from "~/features/meditations/types";

import { PickerScreen } from "./picker-screen";
import { SelectableCard } from "./selectable-card";

// AIDEV-NOTE: Only the generated Part check-in honours this (canonical DPMP / Self-Therapy
// scripts are verbatim, and session-level meditations keep their full opening), so the
// overlay shows the row only when that meditation is selected. Server side: utils/opening.ts.
const OPENING_OPTIONS: readonly {
    opening: MeditationOpening;
    label: string;
    description: string;
}[] = [
    {
        opening: "full",
        label: "Settle in first",
        description:
            "Starts with grounding and a safety check before turning toward the part. Good for a first visit, or a hard day.",
    },
    {
        opening: "brief",
        label: "Brief opening",
        description:
            "A few sentences to settle, then straight to the part. For parts you already know well.",
    },
];

export function openingLabel(opening: MeditationOpening): string {
    return (
        OPENING_OPTIONS.find((option) => option.opening === opening)?.label ??
        "Settle in first"
    );
}

interface OpeningPickerScreenProps {
    selectedOpening: MeditationOpening;
    onSelect: (opening: MeditationOpening) => void;
    onBack: () => void;
}

export function OpeningPickerScreen({
    selectedOpening,
    onSelect,
    onBack,
}: OpeningPickerScreenProps) {
    return (
        <PickerScreen
            title="How the meditation opens"
            hint="Every check-in starts by settling in. Choose how long that takes."
            onBack={onBack}
        >
            {OPENING_OPTIONS.map((option) => (
                <SelectableCard
                    key={option.opening}
                    selected={selectedOpening === option.opening}
                    onSelect={() => onSelect(option.opening)}
                >
                    <p className="text-base font-semibold text-brand-forest-900">
                        {option.label}
                    </p>
                    <p className="mt-2 text-[15px] leading-relaxed text-stone-600">
                        {option.description}
                    </p>
                </SelectableCard>
            ))}
        </PickerScreen>
    );
}
